import { Outlet, useLocation } from "react-router";
import { type FC, useCallback, useMemo, useState } from "react";
import { Layout, Menu, MenuProps } from "antd";
import { Toast, Button } from "antd-mobile";
import {
  CompassOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
  SettingOutlined,
  UserOutlined,
  SearchOutlined,
  CalendarOutlined,
  VideoCameraOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  SignatureOutlined,
} from "@ant-design/icons";
import { FilterOutline, AddOutline } from "antd-mobile-icons";
import classNames from "classnames";
import { isHomeHealthExternalUser } from "../../constants/sso";

// Local imports
import styles from "./styles.module.scss";
import { Header as HeaderMobile } from "../../components/LayoutComponents/Header/index.mobile";
import { MobileLeftMenu } from "../../components/LayoutComponents/LeftMenu/index.mobile";
import { Footer } from "../../components/LayoutComponents/Footer/index.mobile";
import { HeaderWeb } from "../../components/LayoutComponents/HeaderWeb";
import { useHook } from "./useHook";
import VisitNotesSearchModal from "../../components/CustomComponents/VisitNotesSearchModal";
import {
  neoPremiumTab,
  premiumTabs as premiumTabsOriginal,
  settingsTabs,
  tabs as tabsOriginal,
  tabsNP,
  tabsWeb as tabsWebOriginal,
  tabsWebNP,
} from "./tabs";
import SurveyScheduler from "../../components/CustomComponents/SurveyScheduler";
import {
  amplifyProvider,
  authController,
  circadiaEngineProvider,
  clinicController,
  localStorageProvider,
  notificationController,
  patientController,
  pushNotificationProvider,
  timeProvider,
  watchlistController,
  electronEngineProvider,
  summaryController,
  webIntercomProvider,
  searchController,
  homeHealthController,
  analyticsController,
  smsController,
  userController,
} from "../../AppController";
import { useAuth } from "../../hooks/auth";
import { useRouterHook } from "../../hooks/useRouterHook";
import { AppErrorBoundary } from "../../components/CustomComponents/PageErrorBoundry";
import { ErrorComponent } from "../../components/CustomComponents/PageErrorBoundry/ErrorComponent";
import { NetworkBar } from "../../components/CustomComponents/NetworkBar";
import { appNotification } from "../../util/appNotification";
import { useScrollTop } from "../../hooks/useScrollTop";
import { useScreenTimeMetrics } from "../../hooks/useScreenTimeMetrics";
import { routes } from "../../router/mobile/routes";
import { ScreenLoaderWhite } from "../../components/CustomComponents/ScreenLoaderWhite";
import { useFCMTokenUpdate } from "../../hooks/fcmTokenListener";
import { sendZapierTrigger } from "../../util/zapierMain";
import { isMobile, isTablet, isWeb } from "../../util/layout";
import { TMobileRouterPath, useAppNavigate } from "../../hooks/router";
import logo from "../../assets/logo.svg";
import { useIntercom } from "../../hooks/useIntercom";
import { SearchPatientModal } from "../../components/CustomComponents/SearchPatientModal";
import { useSearchHook } from "./useSearchHook";
import { useScreenTimeout } from "../../hooks/useScreenTimeout";
import { useSiderAutoCollapse } from "../../hooks/useSiderAutoCollapse";
import { useMobileTopBottomColor } from "../../hooks/useMobileTopBottomColor";
import { useRecordBackLocation } from "../../hooks/useRecordBackLocation";
import { isPIN } from "../../util/pin";
import { trackWatchlistAdd } from "../../util/engagementTracking";
import { shouldShowNpSchedulingSidebarLink } from "../../util/common";
import { isHomeHealthAtRiskOnlyUser } from "../../util/atRiskOverrideHelpers";
import {
  hasCompassusNavigatorBetaFeature,
  hasCompassusProductivityBetaFeature,
  hasProjectNeoAccess,
  homehealthClinicHasNeoActivated,
} from "../../util/subdomain";
import { useVitalsAIMode } from "../../hooks/useVitalsAIMode";
import { useAppSelector, useAppDispatch } from "../../hooks/redux";
import { setBottomNavKeys } from "../../redux/Setting/setting.slice";
import { MobileNavCustomizerSheet } from "../../components/CustomComponents/MobileNavCustomizerSheet";
import {
  MainHeaderOverrideProvider,
  useMainHeaderOverride,
} from "../../contexts/MainHeaderOverrideContext";
import { AndroidRefreshButton } from "../../pages/CompassusPageHeader/AndroidRefreshButton";
import { isInternalCipUser } from "../../util/navigatorCustomTodoAccess";

const { Sider, Content } = Layout;

const prefetchAmcharts = () => import("@amcharts/amcharts5");
if (typeof window !== "undefined") {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(() => prefetchAmcharts(), { timeout: 4000 });
  } else {
    setTimeout(prefetchAmcharts, 2000);
  }
}

type MenuItem = Required<MenuProps>["items"][number];

const shouldShowCompassusHeaderRefresh = (pathname: string): boolean =>
  pathname === routes.visitNotes.path ||
  pathname === routes.compassusNavigator.path ||
  pathname === routes.telehealth.path ||
  pathname.startsWith("/compassus-patient");

const MainLayoutHeader: FC<{
  onSearchPatientModalOpen: () => void;
  location: ReturnType<typeof useLocation>;
  isHomeHealthExternal: boolean;
}> = ({ onSearchPatientModalOpen, location, isHomeHealthExternal }) => {
  const { headerOverride, hideHeaderContainer } = useMainHeaderOverride();
  const hasOverride = headerOverride !== null && headerOverride !== undefined;
  const hideSearchBar = location.pathname === routes.neo.path;
  const forceHeaderVisibleForNeo = location.pathname === routes.neo.path;
  const hideFacilitySelector =
    location.pathname === routes.newVisit.path ||
    location.pathname === routes.visitCalendar.path ||
    location.pathname === routes.npProductivity.path ||
    location.pathname === routes.npProductivityV2.path ||
    location.pathname === routes.npDashboard.path ||
    location.pathname === routes.neo.path ||
    location.pathname.startsWith("/visit-notes/note/") ||
    location.pathname.startsWith("/visit-notes/pdf-viewer");
  const showCompassusHeaderRefresh =
    !hideFacilitySelector &&
    shouldShowCompassusHeaderRefresh(location.pathname);

  if (hasOverride && hideHeaderContainer) {
    return <>{headerOverride}</>;
  }

  return (
    <div
      className={classNames(styles.headerContainer, {
        [styles.headerHidden]:
          (isHomeHealthExternal && !hasOverride && !forceHeaderVisibleForNeo) ||
          hideHeaderContainer,
        [styles.headerContainerTransparent]: hideSearchBar && !hasOverride,
      })}
    >
      {hasOverride ? (
        <>{headerOverride}</>
      ) : (
        <HeaderWeb
          onClickSearch={onSearchPatientModalOpen}
          hideFacilitySelector={hideFacilitySelector}
          hideSearchBar={hideSearchBar}
          leftOfFacilitySelector={
            showCompassusHeaderRefresh ? (
              <AndroidRefreshButton eventName="Compassus Header - Refresh Page Clicked" />
            ) : null
          }
        />
      )}
    </div>
  );
};

let lastMainPath = "";

// Mobile bottom-bar customization: at most N user-pinned items, plus a
// permanent "More" entry that opens the customizer sheet.
const MOBILE_NAV_MAX_PINNED = 5;
const NAV_MORE_KEY = "__nav_more__";

const createMenuItems = (
  items:
    | typeof tabsWebOriginal
    | typeof premiumTabsOriginal
    | typeof settingsTabs
    | typeof tabsWebNP,
  collapsed: boolean,
  setRouteActive: (route: string) => void
): MenuItem[] => {
  return items.map((item) => ({
    label: item.title,
    icon: item.icon,
    key: item.key,
    onClick: () => setRouteActive(item.key.replace(/\/:\w+\??/g, "")), // remove any optional params from route
    className: classNames(
      styles.menuItem,
      !collapsed && styles.menuItemCollapsed
    ),
  }));
};

const MobileLayout: FC = () => {
  // Check if user is in Vitals AI mode to skip PIN requirement
  const isVitalsAIMode = useVitalsAIMode();

  useMobileTopBottomColor({
    circadiaEngineProvider: circadiaEngineProvider,
  });
  useFCMTokenUpdate({
    electronEngineProvider: electronEngineProvider,
    pushNotificationProvider: pushNotificationProvider,
  });
  const { populateAppData } = useRouterHook({
    amplifyProvider: amplifyProvider,
    authController: authController,
    clinicController: clinicController,
    localStorageProvider: localStorageProvider,
    notificationController: notificationController,
    watchlistController: watchlistController,
    timeProvider: timeProvider,
    appNotification: appNotification,
    patientController: patientController,
    circadiaEngineProvider,
    summaryController,
    homeHealthController,
    isPIN,
    smsController: smsController,
    userController: userController,
  });
  const location = useLocation();

  // Remember the last route outside excluded detail pages (see
  // backNavigation.ts) so their Back buttons can return the user to
  // where they were before opening them.
  useRecordBackLocation();

  const {
    isSearchPatientModalOpen,
    onSearchPatientModalOpen,
    onSearchPatientModalClose,

    searchValue,
    onChange,
    response,

    isInWatchlist,
    addToWatchlist,
    removeFromWatchlist,
    isAddingList,
  } = useSearchHook({
    searchController: searchController,
    analyticsController,
    watchlistController,
    appNotification,
    trackWatchlistAdd: trackWatchlistAdd,
  });
  const {
    menuOpen,
    user,
    userClinic,
    toggleMenu,
    onClickSearch,
    showHeaderBar,
    showFooter,
    onRightIconClick,
    uniquePageKey,
    confirmLogout,
    onCancel,
    onLogoutClick,
    setConfirmLogout,
    localBetaFeature,
    onLocalBetaFeature,
    footerTapEvent,
    topRightIcon,
    connectionStatus,
    showLoading,
    onPatientClick,
    refreshSummary,
    onSetupClick,
  } = useHook({
    analyticsController,
    location,
    clinicController: clinicController,
    populateAppData,
    summaryController,
    localStorageProvider,
  });

  const betaFeaturesAllowedList = useAppSelector(
    (state) => state.auth.betaFeaturesAllowedList ?? []
  );

  const dispatch = useAppDispatch();
  const bottomNavKeys = useAppSelector((state) => state.settings.bottomNavKeys);
  const [navCustomizerOpen, setNavCustomizerOpen] = useState(false);

  let tabsWeb = tabsWebOriginal;
  let tabs = tabsOriginal;
  let premiumTabs = premiumTabsOriginal;
  if (userClinic?.ehr_id == 0) {
    tabsWeb = tabsWebOriginal.filter((tab) => tab.key !== routes.Insights.path);
    tabs = tabsOriginal.filter((tab) => tab.key !== routes.Insights.path);
    premiumTabs = premiumTabsOriginal.filter(
      (tab) => tab.key !== routes.vitalSync.path
    );
  }

  // Project Neo beta: for users with access, surface Neo in the main menu
  // after Inbox and hide the legacy IQ entry from the AI Agents row.
  const hasNeoAccess = hasProjectNeoAccess({
    email: user?.email,
    betaFeaturesAllowedList,
  });
  const showHomeHealthNeoSidebarItem =
    hasNeoAccess && homehealthClinicHasNeoActivated(userClinic?.id);
  const hasTelehealthAccess =
    Array.isArray(betaFeaturesAllowedList) &&
    betaFeaturesAllowedList.includes("telehealth");
  const {
    menuHover,
    collapsed,
    handleCollapse,
    handleMenuHover,
    onSiderItemClick,
  } = useSiderAutoCollapse();

  useScreenTimeMetrics({ analyticsController });

  // useSummary(10 * 60 * 1000);

  // Wrap isPIN to skip PIN for Vitals AI mode users
  const isPINWithVitalsAICheck = useCallback(
    () => isPIN(isVitalsAIMode),
    [isVitalsAIMode]
  );

  useScreenTimeout({
    localStorageProvider,
    location,
    refreshSummary,
    isPIN: isPINWithVitalsAICheck,
  });
  const { onLogout } = useAuth({
    authController: authController,
    localStorageProvider: localStorageProvider,
    pushNotificationProvider,
    populateAppData,
    analyticsController,
    appNotification,
    circadiaEngineProvider,
    sendZapierTrigger,
  });

  const pathname = location.pathname;
  const buttonIcon =
    pathname === routes.watchlist.path ? (
      <AddOutline className={`${styles.actionButtonIconPlus}`} />
    ) : (
      <FilterOutline className={styles.actionButtonIconFilter} />
    );

  useScrollTop();

  const navigate = useAppNavigate();
  const setRouteActive = useCallback(
    (route: string) => {
      navigate(route);
    },
    [navigate]
  );

  // Check if user belongs to home_health_external group or logged in via Microsoft SSO
  const isHomeHealthExternal = useMemo(() => {
    return isHomeHealthExternalUser(user?.groups, user?.email);
  }, [user?.groups, user?.email]);
  const isInternalNavigatorUser = isInternalCipUser(user);

  // State for visit notes search modal
  const [visitSearchModalOpen, setVisitSearchModalOpen] = useState(false);

  const handleVisitSearchClick = () => {
    setVisitSearchModalOpen(true);
  };

  const handleVisitSearchSelect = (note: any) => {
    setVisitSearchModalOpen(false);
    navigate(`/visit-notes/note/${note.noteId}`);
  };

  const showNpSchedulingSidebarLink = shouldShowNpSchedulingSidebarLink(
    user,
    betaFeaturesAllowedList
  );

  // Billing audience for the main-menu "Documentation" entry (the /billing
  // BillingModule inbox): MDs in the BillingPractitioner group and Circadia
  // internal account managers. BPs additionally have Scheduling hidden.
  const isBillingPractitioner =
    user?.groups?.includes("MedicalGroup-BillingPractitioner") ?? false;
  const showBillingSidebarLink =
    isBillingPractitioner ||
    (user?.groups?.includes("AccountManager") ?? false);

  // VRN home-health restricted view: hide Scheduling and Insights entries
  // from the side navigation; everything else remains. See
  // documentation/VRN_HOMEHEALTH_AT_RISK_REVIEW.md.
  const isVrnHomeHealthAtRiskUser = isHomeHealthAtRiskOnlyUser({
    user,
    clinic: userClinic,
  });

  // Home-health facilities don't use the Dashboard Overview screen, so hide the
  // "Dashboard" (/overview) tab from both the sidebar and the mobile footer.
  const isHomeHealthClinic = userClinic?.clinic_type === "homehealth";

  const schedulingSidebarTab = useMemo(
    () => ({
      key: routes.npScheduling.path,
      title: "Scheduling",
      icon: <CalendarOutlined className={styles.footerIcon} />,
    }),
    []
  );

  const addNeoAfterInbox = useCallback((menuTabs: typeof tabsWebOriginal) => {
    const withoutNeo = menuTabs.filter((tab) => tab.key !== routes.neo.path);
    const inboxIndex = withoutNeo.findIndex(
      (tab) => tab.key === routes.dashboard.path
    );

    if (inboxIndex === -1) {
      return [...withoutNeo, neoPremiumTab];
    }

    return [
      ...withoutNeo.slice(0, inboxIndex + 1),
      neoPremiumTab,
      ...withoutNeo.slice(inboxIndex + 1),
    ];
  }, []);

  const items = useMemo(() => {
    const rawBaseTabs = user?.isNursePractitioner
      ? tabsWebNP
      : isInternalNavigatorUser
      ? [tabsWebNP[0], ...tabsWeb]
      : tabsWeb;
    let baseTabs = isVrnHomeHealthAtRiskUser
      ? rawBaseTabs.filter((tab) => tab.key !== routes.Insights.path)
      : rawBaseTabs;
    if (isHomeHealthClinic) {
      baseTabs = baseTabs.filter((tab) => tab.key !== routes.overview.path);
    }

    const tabsWithNeo = hasNeoAccess ? addNeoAfterInbox(baseTabs) : baseTabs;

    return createMenuItems(tabsWithNeo, collapsed, setRouteActive);
  }, [
    tabsWeb,
    tabsWebNP,
    collapsed,
    setRouteActive,
    isVrnHomeHealthAtRiskUser,
    isHomeHealthClinic,
    user?.isNursePractitioner,
    isInternalNavigatorUser,
    showBillingSidebarLink,
    isBillingPractitioner,
    hasNeoAccess,
    addNeoAfterInbox,
  ]);

  const premiumItems = useMemo(() => {
    // Get user groups for access control
    const userGroups = user?.groups;

    // Some of the premium tabs may be restricted to certain user groups
    // If a tab has a 'groups' property, only include it if the user belongs to one of those groups
    const filteredTabs = premiumTabs.filter(
      ({ groups }) =>
        !groups || groups.some((group) => userGroups?.includes(group))
    );
    const tabsWithNeoHandled = hasNeoAccess
      ? filteredTabs.filter((tab) => tab.key !== routes.iq.path)
      : filteredTabs;

    // VRN home-health restricted view: also hide the Vitals AI, IQ/Neo, and
    // Medications entries from the AI Agents row.
    const vrnHiddenPremiumPaths = new Set<string>([
      routes.vitalSync.path,
      routes.iq.path,
      routes.neo.path,
      routes.medRecAI.path,
    ]);
    const finalTabs = isVrnHomeHealthAtRiskUser
      ? tabsWithNeoHandled.filter((tab) => !vrnHiddenPremiumPaths.has(tab.key))
      : tabsWithNeoHandled;
    const shouldShowSchedulingUnderAiAgents =
      showNpSchedulingSidebarLink &&
      !isVrnHomeHealthAtRiskUser &&
      !isBillingPractitioner;
    const tabsWithScheduling = shouldShowSchedulingUnderAiAgents
      ? [schedulingSidebarTab, ...finalTabs]
      : finalTabs;

    return createMenuItems(tabsWithScheduling, collapsed, setRouteActive);
  }, [
    premiumTabs,
    collapsed,
    setRouteActive,
    user?.groups,
    isVrnHomeHealthAtRiskUser,
    hasNeoAccess,
    showNpSchedulingSidebarLink,
    isBillingPractitioner,
    schedulingSidebarTab,
  ]);

  // Universe of items the user can pin to the mobile bottom bar: the mobile
  // base tabs + the access-filtered AI Agents + (optionally) Scheduling,
  // deduped by route key. Mirrors the sidebar filtering so the same
  // access/feature-flag rules apply.
  const availableNavItems = useMemo<ITabObject[]>(() => {
    const baseFooter = user?.isNursePractitioner
      ? tabsNP
      : isInternalNavigatorUser
      ? [tabsNP[0], ...tabs]
      : tabs;
    let baseFiltered = isVrnHomeHealthAtRiskUser
      ? baseFooter.filter((t) => t.key !== routes.Insights.path)
      : baseFooter;
    if (isHomeHealthClinic) {
      baseFiltered = baseFiltered.filter((t) => t.key !== routes.overview.path);
    }
    const baseWithNeo = hasNeoAccess
      ? addNeoAfterInbox(baseFiltered)
      : baseFiltered;

    const userGroups = user?.groups;
    const vrnHiddenPremiumPaths = new Set<string>([
      routes.vitalSync.path,
      routes.iq.path,
      routes.neo.path,
      routes.medRecAI.path,
    ]);
    const premiumFiltered = premiumTabs
      .filter(
        ({ groups }) =>
          !groups || groups.some((group) => userGroups?.includes(group))
      )
      .filter(
        (t) => !isVrnHomeHealthAtRiskUser || !vrnHiddenPremiumPaths.has(t.key)
      )
      .filter((t) => !hasNeoAccess || t.key !== routes.iq.path);

    const scheduling: ITabObject[] =
      showNpSchedulingSidebarLink &&
      !isVrnHomeHealthAtRiskUser &&
      !isBillingPractitioner
        ? [schedulingSidebarTab]
        : [];

    // The Billing inbox (/billing) is a sidebar-only entry for the billing
    // audience; surface it in the pinnable universe too so it can be a footer
    // tab (it's the default first tab for Billing Practitioners).
    const billing: ITabObject[] = showBillingSidebarLink
      ? [
          {
            key: routes.billingModule.path,
            title: "Documentation",
            icon: <SignatureOutlined className={styles.footerIcon} />,
          },
        ]
      : [];

    const combined = [
      ...billing,
      ...baseWithNeo,
      ...scheduling,
      ...premiumFiltered,
    ];
    const seen = new Set<string>();
    return combined.filter((t) => {
      if (seen.has(t.key)) return false;
      seen.add(t.key);
      return true;
    });
  }, [
    tabs,
    tabsNP,
    premiumTabs,
    user?.isNursePractitioner,
    isInternalNavigatorUser,
    user?.groups,
    isVrnHomeHealthAtRiskUser,
    isHomeHealthClinic,
    showNpSchedulingSidebarLink,
    showBillingSidebarLink,
    isBillingPractitioner,
    hasNeoAccess,
    addNeoAfterInbox,
    schedulingSidebarTab,
  ]);

  // Role-based default footer layout (used when the user hasn't customized).
  //   Normal:               Inbox, Insights, Clinical, Watchlist, Consents
  //   NP / VRN:             Navigator, Inbox, Clinical, Insights, Consents
  //   Billing Practitioner: Billing, Navigator, Inbox, Clinical, Consents
  // Any default item the user can't actually access is dropped, then the set
  // is back-filled from the remaining available items to keep it full.
  const defaultPinnedNavKeys = useMemo<string[]>(() => {
    const availableKeys = availableNavItems.map((t) => t.key as string);

    let ordered: string[];
    if (isBillingPractitioner) {
      ordered = [
        routes.billingModule.path,
        routes.evaluationAndManagement.path,
        routes.dashboard.path,
        routes.clinical.path,
        routes.myFacility.path,
      ];
    } else if (
      user?.isNursePractitioner ||
      isInternalNavigatorUser ||
      isVrnHomeHealthAtRiskUser
    ) {
      ordered = [
        routes.evaluationAndManagement.path,
        routes.dashboard.path,
        routes.clinical.path,
        routes.Insights.path,
        routes.myFacility.path,
      ];
    } else {
      ordered = [
        routes.dashboard.path,
        routes.Insights.path,
        routes.clinical.path,
        routes.watchlist.path,
        routes.myFacility.path,
      ];
    }

    const result = ordered.filter((k) => availableKeys.includes(k));
    for (const k of availableKeys) {
      if (result.length >= MOBILE_NAV_MAX_PINNED) break;
      if (!result.includes(k)) result.push(k);
    }
    return result.slice(0, MOBILE_NAV_MAX_PINNED);
  }, [
    availableNavItems,
    isBillingPractitioner,
    user?.isNursePractitioner,
    isInternalNavigatorUser,
    isVrnHomeHealthAtRiskUser,
  ]);

  // Pinned set: the user's saved order (filtered to still-available items),
  // or — when uncustomized — the role-based default above.
  const pinnedNavKeys = useMemo<string[]>(() => {
    const availableKeys = availableNavItems.map((t) => t.key as string);
    if (bottomNavKeys && bottomNavKeys.length) {
      const valid = bottomNavKeys.filter((k) => availableKeys.includes(k));
      return valid.slice(0, MOBILE_NAV_MAX_PINNED);
    }
    return defaultPinnedNavKeys;
  }, [availableNavItems, bottomNavKeys, defaultPinnedNavKeys]);

  // Footer tabs = pinned items (in order) + a permanent "More" entry.
  const footerTabs = useMemo<ITabObject[]>(() => {
    const byKey = new Map(availableNavItems.map((t) => [t.key as string, t]));
    const pinned = pinnedNavKeys
      .map((k) => byKey.get(k))
      .filter((t): t is ITabObject => !!t);
    return [
      ...pinned,
      {
        key: NAV_MORE_KEY as TMobileRouterPath,
        title: "More",
        icon: <AppstoreOutlined className={styles.footerIcon} />,
      },
    ];
  }, [availableNavItems, pinnedNavKeys]);

  const handleLogout = useCallback(() => {
    setConfirmLogout(false);
    setTimeout(() => {
      onLogout();
    }, 100);
  }, [onLogout]);

  // Home-health-external users have their own sidebar menu (Search, Navigator,
  // Visits, Calendar, optional Neo) that isn't represented in
  // tabsWeb/tabsWebNP/premiumTabs.
  // Include those paths in the lookup so the active highlight follows the
  // current route instead of getting stuck on whichever tab last matched.
  const homeHealthExternalLookupTabs: { key: string }[] = isHomeHealthExternal
    ? [
        ...(hasCompassusNavigatorBetaFeature(betaFeaturesAllowedList)
          ? [{ key: routes.compassusNavigator.path }]
          : []),
        ...(hasCompassusProductivityBetaFeature(betaFeaturesAllowedList)
          ? [{ key: routes.npProductivityV2.path }]
          : []),
        { key: routes.visitNotes.path },
        ...(hasTelehealthAccess ? [{ key: routes.telehealth.path }] : []),
        { key: routes.visitCalendar.path },
        ...(showHomeHealthNeoSidebarItem ? [{ key: routes.neo.path }] : []),
      ]
    : [];

  const billingLookupTabs: { key: string }[] = showBillingSidebarLink
    ? [{ key: routes.billingModule.path }]
    : [];

  // Neo is injected into the main menu dynamically via addNeoAfterInbox rather
  // than living in a static tab array, so it must be added to the active-route
  // lookup universe explicitly or the sidebar item never highlights as selected.
  const neoLookupTabs: { key: string }[] = hasNeoAccess
    ? [{ key: routes.neo.path }]
    : [];

  const nextLastMainPath = [
    ...tabsWeb,
    ...tabsWebNP,
    ...premiumTabs,
    ...settingsTabs,
    ...homeHealthExternalLookupTabs,
    ...billingLookupTabs,
    ...neoLookupTabs,
  ]
    .reverse()
    .find(({ key }) => {
      // Compare the path against the current location using URLPattern for dynamic segments
      if (typeof window !== "undefined" && "URLPattern" in window) {
        const pattern = new (window as any).URLPattern(
          key,
          window.location.origin
        );
        return pattern.test(
          location.pathname as TMobileRouterPath,
          window.location.origin
        );
      }
      // Fallback: simple string comparison if URLPattern is not available
      return (location.pathname as TMobileRouterPath) === key;
    });

  if (nextLastMainPath) {
    lastMainPath = nextLastMainPath.key;
  }

  const { handleToggle } = useIntercom({
    webIntercomProvider: webIntercomProvider,
    analyticsController: analyticsController,
  });

  if (
    location.pathname.includes("summary") ||
    location.pathname.includes("chime") ||
    location.pathname.includes("patient-profile") ||
    location.pathname.includes("user-info") ||
    location.pathname.includes("bed-exit-settings") ||
    location.pathname.includes("np-scheduling")
  ) {
    return (
      <div className={styles.app}>
        <div className={styles.body}>
          <AppErrorBoundary
            FallbackComponent={({ error, resetErrorBoundary }) => (
              <ErrorComponent
                error={error}
                resetErrorBoundary={resetErrorBoundary}
              />
            )}
            resetKeys={[uniquePageKey]}
          >
            <SurveyScheduler />
            <Outlet />
          </AppErrorBoundary>
        </div>
      </div>
    );
  }

  let shouldAttachToTop = false;
  const isAndroidWebView =
    typeof window !== "undefined" &&
    /android/i.test(navigator.userAgent) &&
    (/\bwv\b/i.test(navigator.userAgent) || !!(window as any).AndroidAuth);
  if (
    (isWeb() || isTablet()) &&
    (location.pathname === routes.search.path ||
      location.pathname === routes.changeFacility.path)
  ) {
    // Prevent a -56px upward shift when running inside Android WebView.
    // (WebView sometimes reports a "desktop-ish" width which can mistakenly route to web/tablet layout.)
    shouldAttachToTop = !isAndroidWebView;
  }

  // For home health external users, always show sidebar (even on narrow screens)
  const showSidebarForHomeHealth = isHomeHealthExternal;

  // Determine if we should use sidebar navigation behavior (navigate on click) vs mobile behavior (toggle menu)
  const useSidebarNavigation =
    isWeb() || isTablet() || showSidebarForHomeHealth;

  return (
    <MainHeaderOverrideProvider>
      <div className={styles.app}>
        <NetworkBar connectionStatus={connectionStatus} toast={Toast} />
        {(!isMobile() || showSidebarForHomeHealth) && (
          <Layout>
            <Sider
              width={200}
              theme="light"
              collapsible
              collapsed={collapsed}
              onCollapse={handleCollapse}
              className={classNames(
                styles.sider,
                showSidebarForHomeHealth && styles.homeHealthSider
              )}
              onClick={() => {
                if (collapsed) {
                  onSiderItemClick();
                }
              }}
              id="sider"
              collapsedWidth={80}
              onMouseEnter={isWeb() ? () => handleMenuHover(true) : undefined}
              onMouseLeave={isWeb() ? () => handleMenuHover(false) : undefined}
              onTouchStart={!isWeb() ? () => handleMenuHover(true) : undefined}
              onTouchEnd={!isWeb() ? () => handleMenuHover(false) : undefined}
            >
              <div
                className={classNames(
                  collapsed
                    ? styles.logoContainerCollapsed
                    : styles.logoContainer
                )}
              >
                {menuHover && collapsed ? (
                  <DoubleRightOutlined
                    className={styles.icon}
                    onClick={() => handleCollapse(!collapsed)}
                  />
                ) : (
                  <>
                    <img alt="err" src={logo} width="24" />
                    {!collapsed && (
                      <DoubleLeftOutlined
                        className={styles.icon}
                        onClick={() => handleCollapse(!collapsed)}
                      />
                    )}
                  </>
                )}
              </div>
              <div className={styles.menuSection} style={{ height: "100%" }}>
                {isHomeHealthExternal ? (
                  <div>
                    <Menu
                      defaultSelectedKeys={[lastMainPath]}
                      selectedKeys={[lastMainPath]}
                      mode="inline"
                      items={[
                        {
                          label: "Search",
                          icon: (
                            <SearchOutlined className={styles.footerIcon} />
                          ),
                          key: "search-visits",
                          onClick: () => {
                            handleVisitSearchClick();
                            if (!useSidebarNavigation) {
                              toggleMenu();
                            }
                          },
                          className: classNames(
                            styles.menuItem,
                            !collapsed && styles.menuItemCollapsed
                          ),
                        },
                        ...(hasCompassusNavigatorBetaFeature(
                          betaFeaturesAllowedList
                        )
                          ? [
                              {
                                label: "Navigator",
                                icon: (
                                  <CompassOutlined
                                    className={styles.footerIcon}
                                  />
                                ),
                                key: routes.compassusNavigator.path,
                                onClick: () => {
                                  if (useSidebarNavigation) {
                                    navigate(routes.compassusNavigator.path);
                                  } else {
                                    toggleMenu();
                                  }
                                },
                                className: classNames(
                                  styles.menuItem,
                                  !collapsed && styles.menuItemCollapsed
                                ),
                              },
                            ]
                          : []),
                        ...(hasCompassusProductivityBetaFeature(
                          betaFeaturesAllowedList
                        )
                          ? [
                              {
                                label: "Insights",
                                icon: (
                                  <BarChartOutlined
                                    className={styles.footerIcon}
                                  />
                                ),
                                key: routes.npProductivityV2.path,
                                onClick: () => {
                                  if (useSidebarNavigation) {
                                    navigate(routes.npProductivityV2.path);
                                  } else {
                                    toggleMenu();
                                  }
                                },
                                className: classNames(
                                  styles.menuItem,
                                  !collapsed && styles.menuItemCollapsed
                                ),
                              },
                            ]
                          : []),
                        {
                          label: "Visits",
                          icon: (
                            <span
                              role="img"
                              aria-label="audio-file"
                              className={styles.footerIcon}
                            >
                              <svg
                                viewBox="0 0 16 16"
                                fill="currentColor"
                                width="1em"
                                height="1em"
                              >
                                <path d="M4.38262 3.58877C4.30832 3.58877 4.24753 3.64957 4.24753 3.72387V4.53446C4.24753 4.60876 4.30832 4.66956 4.38262 4.66956H10.8673C10.9416 4.66956 11.0024 4.60876 11.0024 4.53446V3.72387C11.0024 3.64957 10.9416 3.58877 10.8673 3.58877H4.38262ZM7.48988 6.02054H4.38262C4.30832 6.02054 4.24753 6.08133 4.24753 6.15564V6.96622C4.24753 7.04053 4.30832 7.10132 4.38262 7.10132H7.48988C7.56418 7.10132 7.62498 7.04053 7.62498 6.96622V6.15564C7.62498 6.08133 7.56418 6.02054 7.48988 6.02054ZM6.67929 13.7549H2.89655V1.86627H12.3534V7.27019C12.3534 7.3445 12.4142 7.40529 12.4885 7.40529H13.4342C13.5085 7.40529 13.5693 7.3445 13.5693 7.27019V1.19078C13.5693 0.891878 13.3278 0.650391 13.0289 0.650391H2.22106C1.92215 0.650391 1.68066 0.891878 1.68066 1.19078V14.4304C1.68066 14.7293 1.92215 14.9708 2.22106 14.9708H6.67929C6.7536 14.9708 6.81439 14.91 6.81439 14.8357V13.89C6.81439 13.8157 6.7536 13.7549 6.67929 13.7549Z" />
                                <path d="M8.58691 10.3203C8.65432 10.3203 8.70701 10.376 8.70703 10.4414C8.70703 11.5978 9.61508 12.5322 10.7324 12.5322C11.8497 12.5321 12.7578 11.5977 12.7578 10.4414C12.7578 10.3762 12.8098 10.3206 12.877 10.3203H13.3691C13.4363 10.3206 13.4883 10.3762 13.4883 10.4414C13.4883 11.8871 12.4395 13.0803 11.0811 13.2578V14.0186H12.249C12.3259 14.0186 12.3913 14.06 12.4355 14.1191C12.4798 14.1783 12.5059 14.2576 12.5059 14.3428V14.9316C12.5058 14.9609 12.4963 14.9894 12.4795 15.0117C12.4628 15.0338 12.4359 15.0526 12.4014 15.0527H9.06348C9.02866 15.0527 9.00114 15.034 8.98438 15.0117C8.96761 14.9894 8.95902 14.9609 8.95898 14.9316V14.3428C8.95898 14.2577 8.98423 14.1782 9.02832 14.1191C9.07254 14.06 9.13805 14.0186 9.21484 14.0186H10.3506V13.252C9.00796 13.0604 7.97656 11.8751 7.97656 10.4414C7.97659 10.376 8.02833 10.3204 8.0957 10.3203H8.58691ZM10.7324 7.09473C11.532 7.09483 12.1777 7.755 12.1777 8.56738V10.458C12.1777 11.2704 11.532 11.9306 10.7324 11.9307C9.93279 11.9307 9.28613 11.2705 9.28613 10.458V8.56738C9.28613 7.75494 9.93279 7.09473 10.7324 7.09473ZM10.7324 7.84375C10.3363 7.84375 10.0166 8.16936 10.0166 8.56738V10.458C10.0166 10.856 10.3363 11.1807 10.7324 11.1807C11.1285 11.1806 11.4482 10.856 11.4482 10.458V8.56738C11.4482 8.16942 11.1285 7.84385 10.7324 7.84375Z" />
                              </svg>
                            </span>
                          ),
                          key: routes.visitNotes.path,
                          onClick: () => {
                            if (useSidebarNavigation) {
                              navigate(routes.visitNotes.path);
                            } else {
                              toggleMenu();
                            }
                          },
                          className: classNames(
                            styles.menuItem,
                            !collapsed && styles.menuItemCollapsed
                          ),
                        },
                        ...(hasTelehealthAccess
                          ? [
                              {
                                label: "Telehealth",
                                icon: (
                                  <VideoCameraOutlined
                                    className={styles.footerIcon}
                                  />
                                ),
                                key: routes.telehealth.path,
                                onClick: () => {
                                  if (useSidebarNavigation) {
                                    navigate(routes.telehealth.path);
                                  } else {
                                    toggleMenu();
                                  }
                                },
                                className: classNames(
                                  styles.menuItem,
                                  !collapsed && styles.menuItemCollapsed
                                ),
                              },
                            ]
                          : []),
                        {
                          label: "Calendar",
                          icon: (
                            <CalendarOutlined className={styles.footerIcon} />
                          ),
                          key: routes.visitCalendar.path,
                          onClick: () => {
                            if (useSidebarNavigation) {
                              navigate(routes.visitCalendar.path);
                            } else {
                              toggleMenu();
                            }
                          },
                          className: classNames(
                            styles.menuItem,
                            !collapsed && styles.menuItemCollapsed
                          ),
                        },
                        ...(showHomeHealthNeoSidebarItem
                          ? [
                              {
                                label: "Neo",
                                icon: neoPremiumTab.icon,
                                key: routes.neo.path,
                                onClick: () => {
                                  if (useSidebarNavigation) {
                                    navigate(routes.neo.path);
                                  } else {
                                    toggleMenu();
                                  }
                                },
                                className: classNames(
                                  styles.menuItem,
                                  !collapsed && styles.menuItemCollapsed
                                ),
                              },
                            ]
                          : []),
                      ]}
                      className={styles.menu}
                    />
                  </div>
                ) : (
                  <div>
                    <Menu
                      defaultSelectedKeys={[lastMainPath]}
                      selectedKeys={[lastMainPath]}
                      mode="inline"
                      items={items}
                      className={styles.menu}
                    />

                    <div className={styles.menuDivider}>
                      {!collapsed && (
                        <span className={styles.menuDividerText}>
                          AI Agents
                        </span>
                      )}
                      <span className={styles.menuDividerLine} />
                    </div>
                    <Menu
                      defaultSelectedKeys={[lastMainPath]}
                      selectedKeys={[lastMainPath]}
                      mode="inline"
                      items={premiumItems}
                      className={styles.menu}
                    />
                  </div>
                )}
                <span className={styles.settingsMenuDividerLine} />
                <Menu
                  defaultSelectedKeys={[lastMainPath]}
                  selectedKeys={[lastMainPath]}
                  className={classNames(styles.menu, styles.menuSupport)}
                  mode="inline"
                  items={[
                    {
                      label: "Settings",
                      icon: <SettingOutlined className={styles.footerIcon} />,
                      key: routes.settings.path,
                      onClick: () => {
                        if (useSidebarNavigation) {
                          navigate(routes.settings.path);
                        } else {
                          toggleMenu();
                        }
                      },
                      className: classNames(
                        styles.menuItem,
                        !collapsed && styles.menuItemCollapsed
                      ),
                    },
                    {
                      label: "Knowledge Hub",
                      icon: <ReadOutlined className={styles.footerIcon} />,
                      key: "knowledge-hub",
                      onClick: () => {
                        window.open(
                          isHomeHealthExternal
                            ? "https://help.circadia.health/compassus/en/collections/18520749-getting-started"
                            : "https://help.circadia.health",
                          "_blank"
                        );
                      },
                      className: classNames(
                        styles.menuItem,
                        !collapsed && styles.menuItemCollapsed
                      ),
                    },
                    ...(!isHomeHealthExternal
                      ? [
                          {
                            label: "Help",
                            icon: (
                              <QuestionCircleOutlined
                                className={styles.footerIcon}
                              />
                            ),
                            key: "help-docs",
                            onClick: () => {
                              handleToggle();
                            },
                            className: classNames(
                              styles.menuItem,
                              !collapsed && styles.menuItemCollapsed
                            ),
                          },
                        ]
                      : []),
                  ]}
                />
              </div>
            </Sider>
            <Layout
              className={collapsed ? "" : "sidebar-expanded"}
              style={
                {
                  "--sidebar-width": collapsed ? "80px" : "200px",
                } as React.CSSProperties
              }
            >
              <Content
                className={classNames(styles.content, {
                  [styles.attachedTop]: shouldAttachToTop,
                })}
              >
                <div id="body">
                  <MainLayoutHeader
                    onSearchPatientModalOpen={onSearchPatientModalOpen}
                    location={location}
                    isHomeHealthExternal={isHomeHealthExternal}
                  />
                  <AppErrorBoundary
                    FallbackComponent={({ error, resetErrorBoundary }) => (
                      <ErrorComponent
                        error={error}
                        resetErrorBoundary={resetErrorBoundary}
                        connectionStatus={connectionStatus}
                      />
                    )}
                    resetKeys={[uniquePageKey]}
                  >
                    {showLoading ? (
                      <ScreenLoaderWhite />
                    ) : (
                      <>
                        <SurveyScheduler />
                        <Outlet />
                      </>
                    )}
                  </AppErrorBoundary>
                </div>
              </Content>
            </Layout>
          </Layout>
        )}
        {isMobile() && !showSidebarForHomeHealth && (
          <>
            <div className={styles.body} id="body">
              {showHeaderBar() && !isHomeHealthExternal && (
                <HeaderMobile
                  onClickSearch={onClickSearch}
                  hideSearch={pathname === routes.neo.path}
                  inlineTitle={pathname === routes.neo.path ? "Neo" : undefined}
                  rightIcon={
                    topRightIcon !== undefined &&
                    false && (
                      <div className="headerRightButtonCon">
                        <Button
                          fill="none"
                          onClick={onRightIconClick}
                          data-test-id={
                            pathname === routes.watchlist.path
                              ? "button-add"
                              : "button-filter"
                          }
                        >
                          {buttonIcon}
                        </Button>
                      </div>
                    )
                  }
                  leftIcon={
                    <span id="menu-icon">
                      {/* <MenuOutlined className="searchIcon" onClick={toggleMenu} /> */}
                      <UserOutlined
                        className="searchIcon"
                        onClick={toggleMenu}
                      />
                    </span>
                  }
                />
              )}
              <AppErrorBoundary
                FallbackComponent={({ error, resetErrorBoundary }) => (
                  <ErrorComponent
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    connectionStatus={connectionStatus}
                  />
                )}
                resetKeys={[uniquePageKey]}
              >
                {showLoading ? (
                  <ScreenLoaderWhite />
                ) : (
                  <>
                    <SurveyScheduler />
                    <Outlet />
                  </>
                )}
              </AppErrorBoundary>
            </div>
            {showFooter() && (
              <div className={styles.bottom}>
                <Footer
                  tabs={footerTabs}
                  tapEvent={footerTapEvent}
                  moreKey={NAV_MORE_KEY}
                  onMore={() => setNavCustomizerOpen(true)}
                />
              </div>
            )}
          </>
        )}

        <MobileLeftMenu
          user={user}
          userClinic={userClinic}
          open={menuOpen}
          onToggle={toggleMenu}
          onLogout={onLogoutClick}
          confirmLogout={confirmLogout}
          onCancel={onCancel}
          onConfirmLogout={handleLogout}
          analyticsController={analyticsController}
          circadiaEngineProvider={circadiaEngineProvider}
          localBetaFeature={localBetaFeature}
          onLocalBetaFeature={onLocalBetaFeature}
          showNpSchedulingLink={showNpSchedulingSidebarLink}
        />
        <SearchPatientModal
          isOpen={isSearchPatientModalOpen}
          onClose={onSearchPatientModalClose}
          searchValue={searchValue}
          onChange={onChange}
          response={response}
          isInWatchlist={isInWatchlist}
          addToWatchlist={addToWatchlist}
          removeFromWatchlist={removeFromWatchlist}
          isAddingList={isAddingList}
          onPatientClick={onPatientClick}
          onSetupClick={onSetupClick}
        />

        {/* Visit Notes Search Modal */}
        <VisitNotesSearchModal
          open={visitSearchModalOpen}
          onClose={() => setVisitSearchModalOpen(false)}
          onSelectNote={handleVisitSearchSelect}
        />

        {/* Bottom-bar customizer (opened from the "More" tab) */}
        <MobileNavCustomizerSheet
          open={navCustomizerOpen}
          onClose={() => setNavCustomizerOpen(false)}
          allItems={availableNavItems}
          pinnedKeys={pinnedNavKeys}
          maxPinned={MOBILE_NAV_MAX_PINNED}
          onSave={(keys) => dispatch(setBottomNavKeys(keys))}
          onReset={() => {
            dispatch(setBottomNavKeys(null));
            setNavCustomizerOpen(false);
          }}
        />
      </div>
    </MainHeaderOverrideProvider>
  );
};

export default MobileLayout;
